import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Check, Loader2, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * BiweeklyAssessment — 2-item wellbeing assessment every 14 days.
 *
 * Item 1: Cantril Self-Anchoring Scale (0-10, public domain, ISTAT BES norm)
 * Item 2: Purpose / sense (1-7 Likert, custom item — NOT WEMWBS/SWLS for commercial safety)
 *
 * Comparisons shown after submit:
 *   - vs. self previous (delta)
 *   - vs. pilot aggregate (k≥5 anonymized)
 *   - vs. Italian ISTAT BES norm (Cantril only)
 */
export const BiweeklyAssessmentDialog = ({ open, onOpenChange, onSubmitted }) => {
  const [step, setStep] = useState(0);
  const [cantril, setCantril] = useState(5);
  const [purpose, setPurpose] = useState(4);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await api.post("/biweekly/submit", { cantril, purpose });
      toast.success("Grazie! Ho registrato il tuo questionario 🌱");
      onSubmitted?.();
      onOpenChange?.(false);
      // reset for next time
      setStep(0); setCantril(5); setPurpose(4);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Salvataggio fallito");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md sm:max-w-lg" data-testid="biweekly-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-rose-500" />
            Questionario bisettimanale
          </DialogTitle>
          <DialogDescription>
            Due brevi domande ogni 2 settimane (20 secondi). Le tue risposte ti
            permettono di vedere come stai cambiando nel tempo.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 mb-2">
          {[0, 1].map((i) => (
            <div key={`bw-prog-${i}`} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-rose-500" : "bg-rose-200"}`} />
          ))}
        </div>

        {step === 0 && (
          <div className="space-y-4" data-testid="biweekly-step-cantril">
            <div>
              <p className="text-sm font-medium leading-relaxed">
                Pensa alla tua vita in questo momento, nel suo complesso.
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed mt-2">
                Su una scala da <strong>0 (la peggior vita possibile)</strong> a <strong>10 (la migliore vita possibile)</strong>,
                <br />dove ti collochi adesso?
              </p>
            </div>
            <div className="space-y-2">
              <input
                type="range"
                min={0}
                max={10}
                step={1}
                value={cantril}
                onChange={(e) => setCantril(Number(e.target.value))}
                className="w-full accent-rose-500"
                data-testid="biweekly-cantril-slider"
              />
              <div className="flex justify-between text-[10px] text-muted-foreground">
                <span>0 peggiore</span>
                <span>5 mediana</span>
                <span>10 migliore</span>
              </div>
              <div className="text-center">
                <span className="text-4xl font-bold text-rose-600" data-testid="biweekly-cantril-value">{cantril}</span>
                <span className="text-base text-muted-foreground"> / 10</span>
              </div>
            </div>
            <Button
              onClick={() => setStep(1)}
              className="w-full bg-rose-500 hover:bg-rose-600"
              data-testid="biweekly-next-btn"
            >
              Continua
            </Button>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-4" data-testid="biweekly-step-purpose">
            <p className="text-sm leading-relaxed">
              <strong>Nelle ultime 2 settimane</strong>, le tue attività ti sono sembrate avere un senso e una direzione?
            </p>
            <div className="grid grid-cols-7 gap-1.5">
              {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                <button
                  key={`p-${n}`}
                  onClick={() => setPurpose(n)}
                  className={`h-10 rounded-md border-2 text-sm font-semibold transition-colors ${
                    purpose === n
                      ? "border-rose-500 bg-rose-500 text-white"
                      : "border-muted bg-white hover:border-rose-300"
                  }`}
                  data-testid={`biweekly-purpose-${n}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>1 per niente</span>
              <span>4 in parte</span>
              <span>7 completamente</span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(0)} disabled={submitting} data-testid="biweekly-back-btn">
                Indietro
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 bg-rose-500 hover:bg-rose-600"
                data-testid="biweekly-submit-btn"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />}
                {submitting ? "Salvo..." : "Conferma"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// =================== BIWEEKLY STATUS BADGE (in Home/Benessere) ===================
export const BiweeklyReminderCard = () => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);

  const fetchStatus = () => {
    api.get("/biweekly/status").then((r) => setStatus(r.data)).catch(() => setStatus(null));
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  if (!status || !status.due) return null;

  return (
    <>
      <Card
        className="border-2 border-rose-200 bg-rose-50/40 cursor-pointer hover:bg-rose-50 transition-colors"
        onClick={() => setOpen(true)}
        data-testid="biweekly-reminder-card"
      >
        <CardContent className="p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">Questionario bisettimanale</p>
            <p className="text-xs text-muted-foreground">
              {status.last_at
                ? `Sono passati ${status.days_since_last} giorni. Vuoi compilarlo? (20 sec)`
                : "Il primo questionario è pronto. Vuoi iniziare? (20 sec)"}
            </p>
          </div>
        </CardContent>
      </Card>
      <BiweeklyAssessmentDialog open={open} onOpenChange={setOpen} onSubmitted={fetchStatus} />
    </>
  );
};

// =================== BIWEEKLY HISTORY + COMPARISONS (in Profile tab) ===================
export const BiweeklyHistoryPanel = () => {
  const [history, setHistory] = useState([]);
  const [comparisons, setComparisons] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get("/biweekly/history"),
      api.get("/biweekly/comparisons"),
    ]).then(([h, c]) => {
      setHistory(h.data.history || []);
      setComparisons(c.data);
    }).catch(() => null)
      .finally(() => setLoading(false));
  };

  useEffect(() => { reload(); }, []);

  if (loading) return <p className="text-xs text-muted-foreground">Caricamento...</p>;

  return (
    <div className="space-y-3" data-testid="biweekly-history-panel">
      <Card className="border">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Questionario bisettimanale</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button
            onClick={() => setDialogOpen(true)}
            className="w-full bg-rose-500 hover:bg-rose-600"
            data-testid="biweekly-compile-btn"
          >
            <Sparkles className="w-4 h-4 mr-1.5" />
            Compila un nuovo questionario
          </Button>

          {comparisons?.unlocked && comparisons.current && (
            <div className="space-y-2 pt-2 border-t" data-testid="biweekly-comparisons">
              <p className="text-xs font-semibold">Ultimo risultato</p>
              <div className="grid grid-cols-2 gap-3">
                <ScoreBox
                  label="Soddisfazione di vita"
                  value={`${comparisons.current.cantril}/10`}
                  data-testid="bw-current-cantril"
                />
                <ScoreBox
                  label="Senso e direzione"
                  value={`${comparisons.current.purpose}/7`}
                  data-testid="bw-current-purpose"
                />
              </div>
              <ComparisonRows comparisons={comparisons} />
            </div>
          )}

          {history.length > 0 && (
            <div className="pt-2 border-t">
              <p className="text-xs font-semibold mb-2">Storico</p>
              <div className="space-y-1 max-h-48 overflow-y-auto">
                {history.slice().reverse().map((h) => (
                  <div key={h.assessment_id} className="flex items-center justify-between text-xs py-1.5 border-b last:border-b-0">
                    <span className="text-muted-foreground">{h.date}</span>
                    <span>Vita: <strong>{h.cantril}</strong>/10 · Senso: <strong>{h.purpose}</strong>/7</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <BiweeklyAssessmentDialog open={dialogOpen} onOpenChange={setDialogOpen} onSubmitted={reload} />
    </div>
  );
};

const ScoreBox = ({ label, value, "data-testid": testid }) => (
  <div className="border rounded-md p-2.5 text-center" data-testid={testid}>
    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{label}</p>
    <p className="text-xl font-bold text-rose-600 mt-1">{value}</p>
  </div>
);

const ComparisonRows = ({ comparisons }) => (
  <div className="space-y-1.5 text-xs">
    {comparisons.vs_self_previous && (
      <ComparisonRow
        label="vs. tua scorsa rilevazione"
        cantrilDelta={comparisons.vs_self_previous.cantril_delta}
        purposeDelta={comparisons.vs_self_previous.purpose_delta}
        testid="bw-vs-self"
      />
    )}
    {comparisons.vs_pilot && (
      <ComparisonRow
        label={`vs. media Revela (n=${comparisons.vs_pilot.n_users})`}
        cantrilDelta={comparisons.vs_pilot.cantril_delta_vs_pilot}
        purposeDelta={comparisons.vs_pilot.purpose_delta_vs_pilot}
        testid="bw-vs-pilot"
      />
    )}
    {!comparisons.vs_pilot && comparisons.vs_pilot_n_below_k !== null && (
      <p className="text-[10px] text-muted-foreground italic" data-testid="bw-pilot-locked">
        Confronto con media Revela disponibile quando almeno {comparisons.k_threshold} utenti hanno compilato (ora: {comparisons.vs_pilot_n_below_k}).
      </p>
    )}
    {comparisons.vs_istat_norm && (
      <ComparisonRow
        label={`vs. norma italiana (${comparisons.vs_istat_norm.norm_value})`}
        cantrilDelta={comparisons.vs_istat_norm.cantril_delta_vs_istat}
        source={comparisons.vs_istat_norm.source}
        testid="bw-vs-istat"
      />
    )}
  </div>
);

const ComparisonRow = ({ label, cantrilDelta, purposeDelta, source, "data-testid": testid }) => {
  const arrow = (d) => {
    if (d === null || d === undefined) return null;
    if (d > 0.1) return <ArrowUp className="w-3 h-3 text-emerald-600" />;
    if (d < -0.1) return <ArrowDown className="w-3 h-3 text-rose-600" />;
    return <Minus className="w-3 h-3 text-slate-500" />;
  };
  const fmt = (d) => d === null || d === undefined ? "—" : `${d > 0 ? "+" : ""}${d}`;
  return (
    <div className="border-l-2 border-rose-200 pl-2 py-0.5" data-testid={testid}>
      <p className="text-[11px] font-medium">{label}</p>
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">Vita {arrow(cantrilDelta)}<strong className="text-foreground">{fmt(cantrilDelta)}</strong></span>
        {purposeDelta !== undefined && (
          <span className="flex items-center gap-1">Senso {arrow(purposeDelta)}<strong className="text-foreground">{fmt(purposeDelta)}</strong></span>
        )}
      </div>
      {source && <p className="text-[9px] italic text-muted-foreground mt-0.5">{source}</p>}
    </div>
  );
};

export default BiweeklyReminderCard;
